import { RouteObject } from '@rocketlane/rocket-router';
import { ReactNode } from 'react';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { SecureRoute } from 'routes/route-with-permission-check';
import { ROUTE_PATHS } from 'routes/routes-paths';
import { retryImportInsideRoutesDefinition } from 'utils/lazyWithRetry';

// * Move this component to the appropriate file if it is getting too big
type ReportsRoutesLayoutProps = {
  children: ReactNode;
  allowOnlyIfFinancialsEnabled?: boolean;
};

const ReportsRoutesLayout = ({
  allowOnlyIfFinancialsEnabled,
  children,
}: ReportsRoutesLayoutProps) => {
  const [isFinancialsEnabled] = useAccountFeature(AccountFeature.Financials);

  if (allowOnlyIfFinancialsEnabled && !isFinancialsEnabled) {
    return null;
  }

  return <>{children}</>;
};

export const reportsRoutes: RouteObject = {
  path: ROUTE_PATHS.REPORTS,
  async lazy() {
    const { ReportsRoute } = await retryImportInsideRoutesDefinition(
      () => import('./reports'),
    );

    return {
      element: (
        <SecureRoute rule="REPORT:READ">
          <ReportsRoute />
        </SecureRoute>
      ),
    };
  },
  children: [
    {
      index: true,
      async lazy() {
        const { ReportsListPage } = await retryImportInsideRoutesDefinition(
          () => import('./list'),
        );

        return {
          element: (
            <SecureRoute rule="REPORT:READ">
              <ReportsListPage />
            </SecureRoute>
          ),
        };
      },
    },
    {
      path: ROUTE_PATHS.CSAT_REPORT,
      async lazy() {
        const { CsatPage } = await retryImportInsideRoutesDefinition(
          () => import('./csat'),
        );

        return {
          element: (
            <SecureRoute rule="CSAT_REPORT:READ">
              <CsatPage />
            </SecureRoute>
          ),
        };
      },
    },
    {
      path: `${ROUTE_PATHS.TIMESHEET_REPORT}/${ROUTE_PATHS.MATCH_ALL}`,
      async lazy() {
        const { TimesheetsReportRoute } =
          await retryImportInsideRoutesDefinition(() => import('./timesheets'));

        return {
          element: (
            <SecureRoute rule="TIME_TRACKING_REPORT:READ">
              <TimesheetsReportRoute />
            </SecureRoute>
          ),
        };
      },
    },
    {
      path: ROUTE_PATHS.PROJECTS_REPORT,
      async lazy() {
        const { ProjectsReportPage } = await retryImportInsideRoutesDefinition(
          () => import('components/pages/projects-report'),
        );

        return {
          element: (
            <SecureRoute rule="PROJECT_PERFORMANCE_REPORT:READ">
              <ProjectsReportPage />
            </SecureRoute>
          ),
        };
      },
    },
    {
      path: ROUTE_PATHS.SPRINTS_REPORT,
      async lazy() {
        const { SprintsReportPage } = await retryImportInsideRoutesDefinition(
          () => import('components/pages/sprints-report/page'),
        );

        return {
          element: (
            <SecureRoute rule="SPRINT_REPORT:READ">
              <SprintsReportPage />
            </SecureRoute>
          ),
        };
      },
    },
    {
      path: ROUTE_PATHS.USERS_REPORT,
      async lazy() {
        const { UsersReportPage } = await retryImportInsideRoutesDefinition(
          () => import('components/pages/users-report'),
        );

        return {
          element: (
            <SecureRoute rule="PEOPLE_PERFORMANCE_REPORT:READ">
              <UsersReportPage />
            </SecureRoute>
          ),
        };
      },
    },
    {
      path: ROUTE_PATHS.TEMPLATE_INSIGHT,
      async lazy() {
        const { TemplateInsight } = await retryImportInsideRoutesDefinition(
          () => import('components/pages/template-insight'),
        );

        return {
          element: (
            <SecureRoute rule="OPERATIONS_INSIGHT_REPORT:READ">
              <TemplateInsight />
            </SecureRoute>
          ),
        };
      },
    },
    {
      path: `${ROUTE_PATHS.INTERVALS_REPORT}/${ROUTE_PATHS.MATCH_ALL}`,
      async lazy() {
        const { IntervalInsightPage } = await retryImportInsideRoutesDefinition(
          () => import('components/pages/intervals-report/page'),
        );

        return {
          element: (
            <SecureRoute rule="INTERVAL_IQ:READ">
              <IntervalInsightPage />
            </SecureRoute>
          ),
        };
      },
    },
    {
      path: ROUTE_PATHS.CUSTOM_REPORT,
      async lazy() {
        const { CustomReports } = await retryImportInsideRoutesDefinition(
          () => import('components/pages/custom-reports/custom-reports-page'),
        );

        return {
          element: (
            <SecureRoute rule="CUSTOM_REPORTS:READ">
              <CustomReports />
            </SecureRoute>
          ),
        };
      },
    },
    {
      path: ROUTE_PATHS.PORTFOLIO_REPORT,
      async lazy() {
        const { ProfitabilityReportPage } =
          await retryImportInsideRoutesDefinition(
            () => import('components/pages/profitability-report'),
          );

        return {
          element: (
            <ReportsRoutesLayout allowOnlyIfFinancialsEnabled>
              <SecureRoute rule="PORTFOLIO_REPORT:READ">
                <ProfitabilityReportPage />
              </SecureRoute>
            </ReportsRoutesLayout>
          ),
        };
      },
    },
    {
      path: ROUTE_PATHS.UTILIZATION_REPORT,
      async lazy() {
        const { UtilizationReportPage } =
          await retryImportInsideRoutesDefinition(
            () => import('components/pages/utilization-report/page'),
          );

        return {
          element: (
            <SecureRoute rule="UTILIZATION_REPORT:READ">
              <UtilizationReportPage />
            </SecureRoute>
          ),
        };
      },
    },
    {
      path: ROUTE_PATHS.TIMESHEET_SUBMISSION_REPORT,
      async lazy() {
        const { TimesheetSubmissionsReportPage } =
          await retryImportInsideRoutesDefinition(
            () => import('components/pages/timesheet-submissions-report/page'),
          );

        return {
          element: (
            <SecureRoute rule="TIMESHEET_SUBMISSION_REPORT:READ">
              <TimesheetSubmissionsReportPage />
            </SecureRoute>
          ),
        };
      },
    },
    {
      path: `${ROUTE_PATHS.USER_TIME_ANALYTICS_REPORT}/${ROUTE_PATHS.MATCH_ALL}`,
      async lazy() {
        const { UserTimeAnalyticsReportRoute } =
          await retryImportInsideRoutesDefinition(
            () => import('./time-analytics/users-report'),
          );

        return {
          element: (
            <SecureRoute rule="PEOPLE_TIME_ANALYTIC_REPORT:READ">
              <UserTimeAnalyticsReportRoute />
            </SecureRoute>
          ),
        };
      },
    },
    {
      path: `${ROUTE_PATHS.PROJECT_TIME_ANALYTICS_REPORT}/${ROUTE_PATHS.MATCH_ALL}`,
      async lazy() {
        const { ProjectTimeAnalyticsReportRoute } =
          await retryImportInsideRoutesDefinition(
            () => import('./time-analytics/projects-report'),
          );

        return {
          element: (
            <SecureRoute rule="PROJECT_TIME_ANALYTIC_REPORT:READ">
              <ProjectTimeAnalyticsReportRoute />
            </SecureRoute>
          ),
        };
      },
    },
    {
      path: `${ROUTE_PATHS.CATEGORY_TIME_ANALYTICS_REPORT}/${ROUTE_PATHS.MATCH_ALL}`,
      async lazy() {
        const { CategoryTimeAnalyticsReportRoute } =
          await retryImportInsideRoutesDefinition(
            () => import('./time-analytics/categories-report'),
          );

        return {
          element: (
            <SecureRoute rule="CATEGORY_TIME_ANALYTIC_REPORT:READ">
              <CategoryTimeAnalyticsReportRoute />
            </SecureRoute>
          ),
        };
      },
    },
  ],
};
